import React, { useEffect, useRef } from "react";
import Text from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import Avatar from "@material-ui/core/Avatar";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import defaultUserImg from "asset/img/default-avatar.jpg";
import { formatVolunteerTime } from "common/utils";
import type { LeaderboardUser, RankFilter } from "../data/types";

export interface AwardListProps {
  leaderboardData: LeaderboardUser[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  myRankItemId: string | null;
  rankFilter: RankFilter;
  loadMoreRef: React.RefObject<HTMLDivElement>;
}

const AwardList: React.FC<AwardListProps> = (props) => {
  const {
    leaderboardData,
    loading,
    loadingMore,
    hasMore,
    myRankItemId,
    rankFilter,
    loadMoreRef,
  } = props;

  const classes = useStyles();
  const translate = useTranslation().t;
  const myRankRowRef = useRef<HTMLDivElement | null>(null);
  /** Scroll once after a reset fetch (not again on load-more) */
  const myRankScrollDoneRef = useRef(false);

  useEffect(() => {
    if (rankFilter !== "MY_RANK") {
      myRankScrollDoneRef.current = false;
    }
  }, [rankFilter]);

  useEffect(() => {
    if (loading) {
      myRankScrollDoneRef.current = false;
    }
  }, [loading]);

  /**
   * MY_RANK: after data loads, align the current user's row just below the sticky
   * filters bar (uses its live height so we don't show ~1 row of extra gap).
   */
  useEffect(() => {
    if (
      loading ||
      rankFilter !== "MY_RANK" ||
      !myRankItemId ||
      leaderboardData.length === 0 ||
      myRankScrollDoneRef.current
    ) {
      return;
    }
    const el = myRankRowRef.current;
    if (!el) return;
    myRankScrollDoneRef.current = true;

    const scrollRowBelowStickyFilters = () => {
      const sticky = document.querySelector(
        "[data-leaderboard-sticky-filters]"
      ) as HTMLElement | null;
      const stickyBottom =
        sticky?.getBoundingClientRect().bottom ?? 44 + 100;
      const gapPx = 4;
      const targetY = stickyBottom + gapPx;
      const elTop = el.getBoundingClientRect().top;
      const delta = elTop - targetY;
      window.scrollBy({ top: delta, behavior: "smooth" });
    };

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollRowBelowStickyFilters);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [loading, rankFilter, myRankItemId, leaderboardData]);

  const getAvatarUrl = (imageUrl: string | null | undefined): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    const baseUrl = process.env.REACT_APP_DOMAIN_API || "";
    return `${baseUrl}${imageUrl}`;
  };

  const renderLeaderboard = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 16 }}>
          <CircularProgress size={24} color="primary" />
        </div>
      );
    }

    if (!loading && leaderboardData.length === 0) {
      return (
        <p>
          <i>{translate("NO_LEADERBOARD_DATA")}</i>
        </p>
      );
    }

    return (
      <>
        {leaderboardData.map((user, index) => {
          const u = user as LeaderboardUser;
          const ranking = u.rank ?? index + 1;
          const avatarUrl = getAvatarUrl(u.imageUrl);
          const isMyRank =
            rankFilter === "MY_RANK" &&
            myRankItemId != null &&
            (u.id === myRankItemId || u.userId === myRankItemId);

          const lastBadgeName =
            u.badgeHistory && u.badgeHistory.length > 0
              ? u.badgeHistory[u.badgeHistory.length - 1].name
              : "";
          const pointsLabel = u.volunteerMinutes > 0 ? u.volunteerMinutes : "0";
          const hoursLabel =
            typeof u.volunteerMinutes === "number"
              ? formatVolunteerTime(u.volunteerMinutes, translate)
              : "-";
          const validatedLabel =
            typeof u.totalServicesValidatedApproved === "number"
              ? u.totalServicesValidatedApproved.toString()
              : "-";
          const addedLabel =
            typeof u.totalServicesAdded === "number"
              ? u.totalServicesAdded.toString()
              : "-";

          return (
            <div
              key={u.id ?? index}
              ref={isMyRank ? myRankRowRef : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 10px",
                borderBottom:
                  index < leaderboardData.length - 1
                    ? "1px solid #e0e0e0"
                    : "none",
                backgroundColor: isMyRank ? "#FFF9C4" : undefined,
              }}
            >
              <div style={{ marginRight: 16, minWidth: 32 }}>
                <Text variant="h6" style={{ fontWeight: "bold" }}>
                  #{ranking}
                </Text>
              </div>
              <div style={{ marginRight: 16 }}>
                <Avatar
                  src={avatarUrl || defaultUserImg}
                  alt={u.displayName || "User"}
                  className={classes.avatar}
                >
                  {(u.displayName || "U").charAt(0).toUpperCase()}
                </Avatar>
              </div>
              <div style={{ flex: 1 }}>
                <Text style={{ fontWeight: 600, fontSize: 14 }}>
                  {u.displayName || "Unknown User"}{" "}
                  {u.locationText
                    ? translate("FROM") + " " + u.locationText
                    : ""}
                </Text>
                {
                  lastBadgeName && (
                    <Text style={{ fontWeight: 600, fontSize: 13 }}>
                      {translate("BADGE")}: {lastBadgeName}
                    </Text>
                  )
                }
                <Text className={classes.infoTextSecondary}>
                  {`${translate("POINTS")}: ${pointsLabel}, ${translate(
                    "TIME_VOLUNTEERED"
                  )}: ${hoursLabel}`}
                </Text>
                <Text className={classes.infoTextSecondary}>
                  {`${translate("VALIDATED_SERVICES")}: ${validatedLabel}, ${translate(
                    "ADDED_SERVICES"
                  )}: ${addedLabel}`}
                </Text>
              </div>
            </div>
          );
        })}
        {hasMore && leaderboardData.length > 0 && (
          <div ref={loadMoreRef} style={{ minHeight: 1, padding: 8 }}>
            {loadingMore && (
              <div style={{ textAlign: "center" }}>
                <CircularProgress size={24} />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Container className={classes.root}>{renderLeaderboard()}</Container>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      paddingTop: 10,
      paddingBottom: 40,
    },
    loadmore: {
      color: "#6B46E5",
      fontSize: 30,
    },
    infoTextPrimary: {
      fontSize: 13,
      color: "#000000",
      fontWeight: 600,
    },
    infoTextSecondary: {
      fontSize: 13,
      color: "#000000",
    },
    avatar: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      border: "1px solid #dddddd",
    },
  })
);

export default AwardList;
